import React from "react";
import { useTranslate } from "react-admin";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  PersonOutline as AccountIcon,
} from "@mui/icons-material";
import type { CostCenterRecord } from "../types/costCenter";

const i18nBase = "resources.credit-consumption/cost-center";

interface CostCenterAccountsDialogProps {
  record: CostCenterRecord | null;
  onClose: () => void;
}

export const CostCenterAccountsDialog: React.FC<
  CostCenterAccountsDialogProps
> = ({ record, onClose }) => {
  const translate = useTranslate();
  const accounts = record?.accounts ?? [];

  return (
    <Dialog
      open={record !== null}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontWeight: 600,
          pr: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {record
            ? translate(`${i18nBase}.accountsDialog.title`, {
                name: record.name,
              })
            : ""}
          {accounts.length > 0 && (
            <Chip
              label={translate(`${i18nBase}.accountsDialog.count`, {
                smart_count: accounts.length,
              })}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {accounts.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", py: 2, textAlign: "center" }}
          >
            {translate(`${i18nBase}.accountsDialog.empty`)}
          </Typography>
        ) : (
          <List dense disablePadding>
            {accounts.map((account) => (
              <ListItem key={account.id} disableGutters>
                <ListItemIcon sx={{ minWidth: 34 }}>
                  <AccountIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText primary={account.name} />
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CostCenterAccountsDialog;
